// useChannel.ts — bind one live component to its channel.
//
// The hook is thin on purpose: all the state lives in channelStore, so a
// component that unmounts and remounts (a UI replacement, a StrictMode
// double-mount) re-attaches to the same socket and the same buffer rather
// than starting over.

import { useSyncExternalStore, useEffect } from 'react';
// The SPA's own client instance (lib/web exports the factory, not an
// instance), so the socket presents the same token the REST calls do.
import { apiClient } from '../../api';
import type { ChannelState } from './channelStore';
import { acquire, release, snapshot, subscribe } from './channelStore';

/** Where the session Pod's channel sockets live, relative to the SPA. */
export function channelUrl(sessionId: string, channel: string): string {
    return `/api/sessions/${sessionId}/chat/api/channels/${encodeURIComponent(channel)}`;
}

export function useChannel(
    key: string,
    sessionId: string,
    channel: string,
    toolCallId: string,
): ChannelState {
    useEffect(() => {
        if (!channel) return;
        // The token is read here rather than inside channelStore so that
        // module stays free of app imports and unit-testable in node.
        acquire(key, channelUrl(sessionId, channel), toolCallId, apiClient.getToken() ?? '');
        // release() only drops the ref count — the socket survives so the
        // remount half of a UI replacement finds it still open. Actual
        // disconnection is LiveMarkdown's job once the swap has settled.
        return () => release(key);
    }, [key, sessionId, channel, toolCallId]);

    return useSyncExternalStore(
        (listener) => subscribe(key, listener),
        () => snapshot(key),
        () => snapshot(key),
    );
}
